import type { CSSProperties, MouseEvent, RefObject } from 'react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { Classnames } from '@snowit/icetypes';
import type { ButtonEvent, InputOnChange, InputSelectOnChange } from '@snowit/icetypes/events';
import type { IInput } from '../Input/Input';
import type { IInputOption } from '../InputSelect/InputSelect';

import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import ButtonIcon from '@snowit/ui/Buttons/ButtonIcon';
import Divider from '@snowit/ui/Divider';
import Icon from '@snowit/ui/Icon';
import Typo from '@snowit/ui/Typography/Typo';
import fuzzy from '@snowit/utils/fuzzy';
import testId from '@snowit/utils/testId';

import { m } from '../../utils/Motion';
import Input from '../Input/Input';

import cx from 'classnames';
import s from './InputSearch.module.scss';

export type InputSearchOption<T extends any> = IInputOption<T> & {
  search: string;
  divider?: boolean;
};

export type InputSearchProps<T extends any> = IInput & {
  resettable?: boolean;
  external?: boolean;
  classNames?: Classnames<'wrapper' | 'wrapperInput' | 'input' | 'dropdown'>;
  options: InputSearchOption<T>[];
  onSelect: (params: InputSelectOnChange<T>) => void;
  onReset?: () => void;
  forceValue?: string;
};

const InputSearch = <T extends any>(props: InputSearchProps<T>) => {
  const {
    classNames,
    label,
    placeholder,
    disabled,
    readOnly,
    required,
    resettable = true,
    hint,
    error,
    errorMsg,
    test,
    icon,
    size,
    external,
    forceValue,
    onSelect,
    onReset,
  } = props;
  const parent = useRef<HTMLInputElement | null>(null);
  const { minWidth } = useBreakpoints();
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [wParent, setWParent] = useState(0);
  const [searchInput, setSearchInput] = useState<string>('');
  const [temp, setTemp] = useState<string>('');

  useLayoutEffect(() => {
    if (parent.current) {
      setWParent(parent.current.offsetWidth);
    }
  }, []);

  const handleInputs = (input: string | number | undefined | null) => {
    const val = String(input ?? '');
    const found = props.options.find((opt) => opt.value === val);
    setTemp(found?.label ?? val);
    setSearchInput('');
  };

  useEffect(() => {
    handleInputs(props.value);
  }, [props.value, props.options]);

  let motionVariants = {};

  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateY: '0%' },
      collapsed: { opacity: 1, translateY: '100%' },
    };
  }

  const handleDropdown = () => setShowDropdown(true);

  const handleReset = (event: ButtonEvent) => {
    event.stopPropagation();
    setSearchInput('');
    setTemp('');
    onSelect({
      event: event as any,
      key: '',
      value: '',
    });
    if (typeof onReset === 'function') {
      onReset();
    }
  };

  const handleFocus = () => {
    setSearchInput('');
    setTemp('');
    setShowDropdown(true);
  };

  const handleBlur = () => {
    if (!searchInput) handleInputs(props.value);
  };

  const handleChange = (args: InputOnChange) => {
    if (typeof props.onChange === 'function') {
      props.onChange(args);
    }
    setShowDropdown(true);
    setSearchInput(args.value);
    setTemp(args.value);
  };

  const handleSelect = (
    val: string | number | null | undefined,
    option: InputSearchOption<T>,
    event: MouseEvent<HTMLDivElement>,
  ) => {
    if (option.disabled) return;
    if (option.noCta) return;
    setShowDropdown(false);
    handleInputs(val);
    onSelect({
      value: String(val ?? ''),
      entity: option.entity,
      event: event as any,
      key: option.key,
    });
  };

  const renderInput = (
    ref: RefObject<HTMLInputElement | null> | undefined = undefined,
    _clickable = false,
    mobile = false,
  ) => {
    const isClickable = _clickable && !disabled && !readOnly;
    const isResettable = Boolean(props.value && isClickable && resettable);
    return (
      <Input
        classNames={{
          wrapper: classNames?.wrapper,
          wrapInput: classNames?.wrapInput,
          input: classNames?.input,
          label: classNames?.label,
        }}
        dropdown={!(forceValue ?? temp) || !isResettable}
        fakeInput={mobile}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        ref={ref}
        type={'text'}
        onChange={handleChange}
        onClick={isClickable ? handleDropdown : undefined}
        onFocus={isClickable ? handleFocus : undefined}
        onBlur={isClickable ? handleBlur : undefined}
        value={forceValue ?? temp}
        hint={hint}
        error={error}
        errorMsg={errorMsg}
        test={test}
        icon={icon}
        size={size}
        readOnly={readOnly}
        iconButton={'close'}
        onClickButton={isResettable ? (args) => handleReset(args.event) : undefined}
        loading={props.loading}
      />
    );
  };

  const renderModalHeader = () => {
    if (minWidth.sm) return null;
    return (
      <header className={s.header}>
        <Typo
          className={s.title}
          text={placeholder || label || ''}
          tag={'h2'}
          size={'df'}
          weight={'medium'}
          color={'text'}
        />
        <ButtonIcon
          classNames={{ button: s.close }}
          icon={'close'}
          onClick={() => setShowDropdown(false)}
          theme={'ghost'}
          size={'db'}
          color={'text'}
        />
      </header>
    );
  };

  const renderIconOption = (option: InputSearchOption<T>) => {
    if (!option.icon) return null;
    return <Icon className={s.optionIcon} name={option.icon} />;
  };

  const renderLabelOption = (option: InputSearchOption<T>) => {
    if (!option.label) return null;
    return (
      <Typo text={option.label} tag={'label'} size={'df'} lineHeight={'paragraph'} color={'text'} />
    );
  };

  const renderHintOption = (option: InputSearchOption<T>) => {
    if (!option.hint) return null;
    const hints = Array.isArray(option.hint) ? option.hint : [option.hint];
    return hints.map((hint) => (
      <Typo key={hint} text={hint} tag={'p'} size={'sm'} color={'text-l'} />
    ));
  };

  const renderOption = (option: InputSearchOption<T>) => {
    if (option.divider) {
      return (
        <Divider
          key={option.key}
          classNames={{ wrapper: s.divider }}
          theme={'line'}
          spacing={0.5}
        />
      );
    }

    if (typeof option.content === 'function') {
      return (
        <div
          className={cx(s.option, option.disabled && s.optionDisabled)}
          onClick={(event) => handleSelect(option.value, option, event)}
          key={option.key}
        >
          <div className={s.contentOption}>{option.content()}</div>
        </div>
      );
    }

    return (
      <div
        className={cx(s.option, option.disabled && s.optionDisabled)}
        onClick={(event) => handleSelect(option.value, option, event)}
        // selected={option.key === value}
        key={option.key}
        {...testId(test, option.key)}
      >
        <div className={s.contentOption}>
          {renderIconOption(option)}
          <div className={s.wrapLabel}>
            {renderLabelOption(option)}
            {renderHintOption(option)}
          </div>
        </div>
      </div>
    );
  };

  const renderOptions = () => {
    const options = external
      ? props.options
      : props.options.filter((opt) => {
          if (!searchInput) return true;
          return fuzzy(searchInput, opt.search);
        });

    if (!options.length) {
      // return renderOption({
      //   key: 'no_results',
      //   search: '',
      //   label: 'no_results',
      // });
      return [];
    }

    return options.map(renderOption);
  };

  const renderDropdown = () => {
    return (
      <m.div
        className={cx(s.dropDown, classNames?.dropdown)}
        style={
          {
            '--w-parent': wParent,
          } as CSSProperties
        }
        initial='collapsed'
        animate='open'
        exit='collapsed'
        variants={motionVariants}
        transition={{
          type: 'tween',
          duration: 0.25,
          ease: [0.215, 0.61, 0.355, 1],
        }}
      >
        {renderModalHeader()}
        {!minWidth.sm && <div className={s.innerInput}>{renderInput()}</div>}
        <div className={s.body}>{renderOptions()}</div>
      </m.div>
    );
  };

  return (
    <Fragment>
      {renderInput(parent, true, !minWidth.sm)}
      <Portal
        mounted={showDropdown}
        content={renderDropdown}
        onClose={() => setShowDropdown(false)}
        escToHide
        clickToHide
        fullscreen={!minWidth.sm}
        position={'adaptive'}
        bindTo={parent.current}
      />
    </Fragment>
  );
};

export default InputSearch;
